import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { AREA_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { Area, AreaSearch } from 'src/app/model/locations/area';
import { AreaApiService } from 'src/app/services/api/area-api.service';
import { AreaTableService } from 'src/app/services/table/area-table.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { AREA_TABLE_DEFINITION } from '../../definitions/area-table-definition';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { FilterMetadata } from '@openng/optimus-ui/api';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Panel } from '@openng/optimus-ui/panel';
import { AreaMobileFilterComponent } from '../../components/area-mobile-filter/area-mobile-filter.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';

@Component({
  selector: 'app-area-container',
  templateUrl: './area-container.component.html',
  styleUrls: ['./area-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ButtonDirective,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    Panel,
    AreaMobileFilterComponent,
    TranslatePipe,
  ],
})
export class AreaContainerComponent
  extends SearchContainerComponent<AreaSearch>
  implements OnInit
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _areaApi = inject(AreaApiService);
  protected _areaTableService = inject(AreaTableService);
  protected _authApi = inject(AuthApiService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);

  allAreas: any[] = [];
  canCreate = false;
  canEdit = false;
  mobileFilterCollapsed = true;

  ngOnInit(): void {
    this.setTableService(this._areaTableService);

    this.initialize();
  }

  initialize() {
    this._translateService.get('LOCATION.SCREEN.AREA').subscribe((label) => {
      if (this._deviceService.isMobile()) {
        this._breadCrumbBuilderService.resetAndAddBreadCrumb({
          label: label,
          routerLink: null,
        });
      } else {
        this._tabService.updateActiveTabLabel(label);
      }
      this.screenName = label;
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.AREAS.CREATE_AREA,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.AREAS.EDIT_AREA,
    ]);

    this.columns = AREA_TABLE_DEFINITION;
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort: any = {};
    if ($event.lazy && $event.lazy.sortField) {
      sort[$event.lazy.sortField as string] =
        $event.lazy.sortOrder && $event.lazy.sortOrder === 1 ? 'ASC' : 'DESC';
      this._areaTableService.setLastSortField($event.lazy.sortField as string);
      this._areaTableService.setLastSortDirection(1);
    } else {
      sort['name'] = 'ASC';
      this._areaTableService.setLastSortField('name');
      this._areaTableService.setLastSortDirection(1);
    }
    this._areaApi.queryForAreas(req.pageSize, req.page, query, sort).subscribe({
      next: (areaConnection) => {
        const areas = areaConnection.items;
        this.elements = [...areas];
        this.totalRecords = areaConnection.totalRecords;
        this._areaTableService.setLastTotalRecords(this.totalRecords);
        this.loading = false;
      },
      error: (error) => {
        console.error(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'LOCATION.MESSAGES.ERROR.QUERY_AREAS',
        });
        this.loading = false;
      },
    });
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    const query = {
      name: null,
      contactList: null,
      owner: null,
      facilities: null,
      region: null,
    };

    if (filters.name) {
      query.name = (filters.name as FilterMetadata).value;
    }
    if (filters.contactList) {
      query.contactList = (filters.contactList as FilterMetadata).value;
    }
    if (filters.owner) {
      query.owner = (filters.owner as FilterMetadata).value;
    }
    if (filters.facilities) {
      query.facilities = (filters.facilities as FilterMetadata).value;
    }
    if (filters.region) {
      query.region = (filters.region as FilterMetadata).value;
    }

    this.queryNetwork(req, $event, query);
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._areaTableService.getLastLazyLoad()! });
  }

  newArea() {
    const tab = this._tabService.buildNewTab(AREA_DETAIL_CONTAINER, true);
    this._tabService.openTab(tab);
  }

  changeViewEvent($event: { data: Area }) {
    const tab = this._tabService.buildNewTab(
      AREA_DETAIL_CONTAINER,
      true,
      undefined,
      $event.data.id!
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  mobileFilter() {
    const newLazy = {
      filters: {},
    };
    this.mobileFilterCollapsed = true;
    this._areaTableService.setLastLazyLoad(newLazy);
    this.lazyLoad({ lazy: this._areaTableService.getLastLazyLoad()! });
  }
}
